import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, ArrowLeft, Sparkles, Shield, Briefcase } from "lucide-react";
import { api, formatApiError, TOKEN_KEY } from "../lib/api";
import { isDemoMode } from "../lib/links";

const PREFILL_CREDENTIALS = {
    admin: { email: "admin@annuityreviewpro.com", password: "AnnuityPro2026!", label: "FMO Admin" },
    agent: { email: "mark.stevens@demoarp.io", password: "DemoAgent2026!", label: "Demo Agent · Mark Stevens" },
};

export default function AdminLogin() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [demoBanner, setDemoBanner] = useState(null);
    const [showDemoButtons, setShowDemoButtons] = useState(false);
    const formRef = useRef(null);

    useEffect(() => {
        setShowDemoButtons(isDemoMode());
    }, []);

    const demoSignIn = (role) => {
        const creds = PREFILL_CREDENTIALS[role];
        if (!creds) return;
        setEmail(creds.email);
        setPassword(creds.password);
        setDemoBanner(creds.label);
        // Submit on next tick after state update
        setTimeout(() => formRef.current?.requestSubmit(), 200);
    };

    useEffect(() => {
        const prefill = searchParams.get("prefill");
        const tok = localStorage.getItem(TOKEN_KEY);
        if (!tok) return;
        try {
            const role = JSON.parse(atob(tok.split(".")[1]))?.role;
            // If a prefill role is requested but doesn't match the current role,
            // sign out the current session so prefill can re-login as the requested role.
            const wantsRole = prefill === "admin" ? "admin" : prefill === "agent" ? "agent" : null;
            if (wantsRole && wantsRole !== role) {
                localStorage.removeItem(TOKEN_KEY);
                return;
            }
            navigate(role === "agent" ? "/agent" : "/admin", { replace: true });
        } catch { /* ignore */ }
    }, [navigate, searchParams]);

    // Demo prefill: ?prefill=admin or ?prefill=agent → auto-fill creds and submit
    useEffect(() => {
        const prefill = searchParams.get("prefill");
        const creds = PREFILL_CREDENTIALS[prefill];
        if (!creds) return;
        if (localStorage.getItem(TOKEN_KEY)) return; // already signed in
        setEmail(creds.email);
        setPassword(creds.password);
        setDemoBanner(creds.label);
        const t = setTimeout(() => {
            formRef.current?.requestSubmit();
        }, 700);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem(TOKEN_KEY, data.access_token);
            toast.success("Welcome back");
            const role = data?.user?.role;
            const next = searchParams.get("next");
            const fallback = role === "agent" ? "/agent" : "/admin";
            // Only allow same-origin internal paths
            const target = (next && next.startsWith("/") && !next.startsWith("//")) ? next : fallback;
            navigate(target, { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
            setDemoBanner(null);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 grid md:grid-cols-2" data-testid="admin-login-page">
            <div className="hidden md:flex bg-navy-900 text-white p-16 flex-col justify-between relative overflow-hidden">
                <div className="absolute inset-0 grain opacity-30" />
                <div className="relative">
                    <Link to="/" className="inline-flex items-center gap-2 text-blue-100 hover:text-white" data-testid="back-to-site">
                        <ArrowLeft className="w-4 h-4" /> Back to site
                    </Link>
                </div>
                <div className="relative">
                    <ShieldCheck className="w-10 h-10 text-blue-200" />
                    <h1 className="font-serif text-4xl lg:text-5xl mt-6 leading-tight">Sign In</h1>
                    <p className="mt-6 text-blue-100/85 text-lg max-w-md">
                        FMO admins and agents — sign in to review leads, manage agents, and track conversions.
                    </p>
                </div>
                <div className="relative text-sm text-blue-200/70">Your Annuity Review · Internal use only</div>
            </div>

            <div className="flex items-center justify-center p-8">
                <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 sm:p-10 shadow-sm">
                    <h2 className="font-serif text-3xl text-navy-900">Sign in</h2>
                    <p className="mt-2 text-slate-600">Use your admin or agent credentials.</p>
                    {showDemoButtons && !demoBanner && (
                        <div className="mt-6 bg-amber-50 border-2 border-amber-300 rounded-xl p-4" data-testid="demo-signin-panel">
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles className="w-4 h-4 text-amber-600" />
                                <div className="text-xs font-bold uppercase tracking-[0.15em] text-amber-900">Demo · One-Click Sign In</div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => demoSignIn("admin")}
                                    disabled={submitting}
                                    className="flex items-center justify-center gap-2 bg-navy-900 hover:bg-navy-800 text-white rounded-lg px-3 py-2.5 text-sm font-semibold transition shadow-sm disabled:opacity-50"
                                    data-testid="demo-signin-admin"
                                >
                                    <Shield className="w-4 h-4" /> FMO Admin
                                </button>
                                <button
                                    type="button"
                                    onClick={() => demoSignIn("agent")}
                                    disabled={submitting}
                                    className="flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg px-3 py-2.5 text-sm font-semibold transition shadow-sm disabled:opacity-50"
                                    data-testid="demo-signin-agent"
                                >
                                    <Briefcase className="w-4 h-4" /> Demo Agent
                                </button>
                            </div>
                            <p className="mt-2.5 text-[11px] text-amber-900/70 text-center">Investors / prospects · click to enter a live dashboard instantly</p>
                        </div>
                    )}
                    {demoBanner && (
                        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 flex items-center gap-2 text-sm text-amber-900" data-testid="demo-prefill-banner">
                            <Sparkles className="w-4 h-4 text-amber-600" />
                            <span>Demo session — signing in as <span className="font-semibold">{demoBanner}</span>…</span>
                        </div>
                    )}
                    <form ref={formRef} onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
                        <div>
                            <Label htmlFor="email" className="text-base">Email</Label>
                            <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12 text-base" data-testid="login-email" />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-base">Password</Label>
                            <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-12 text-base" data-testid="login-password" />
                        </div>
                        <Button type="submit" disabled={submitting} className="w-full bg-navy-900 hover:bg-navy-800 text-white h-12 text-base font-medium rounded-md" data-testid="login-submit">
                            {submitting ? "Signing in..." : "Sign In"}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
